import {Component, Fragment, h, Prop} from '@stencil/core';
import {Unsubscribe} from '@stencil/redux';
import {openCart} from '../../../../redux/actions';
import {RootState} from '../../../../redux/reducers';
import {store} from '../../../../redux/store';
import {Translations} from '../../../../redux/types';
import {AmountControlValues} from '../../../../types/types';
import {trans} from '../../../../utils/trans';
import TrashIcon from "../../svg-elements/trash";

@Component({
    tag: 'rentware-amount-control-booking-template',
    styleUrl: 'style-booking.sass',
    shadow: false,
})
export class RentwareAmountControlBookingTemplate {
    @Prop() controlValues: AmountControlValues;
    @Prop() onIncreaseValue: (number) => void;
    @Prop() onInputChange: (event) => void;
    @Prop() onValidateKey: (event) => void;
    @Prop() itemInCart: boolean;

    @Prop({mutable: true}) locale: string;
    @Prop({mutable: true}) translations: Translations;

    unsubscribe!: Unsubscribe;
    openCart!: typeof openCart;

    componentWillLoad() {
        this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
            return {
                locale: state.app.locale,
                translations: state.system.translations,
            };
        });

        store.mapDispatchToProps(this, {
            openCart,
        });
    }

    get containerClass() {
        let containerClass = "RACBContainer";

        if (this.itemInCart) {
            containerClass += " RACBDefaultColors";
        } else {
            containerClass += " RACBCtaColors";
        }

        if (!this.available) {
            containerClass += " RACBNotAvailableContainer";
        }

        return containerClass;
    }

    get minusButtonEnabled() {
        return this.controlValues.itemAmount !== 0 &&
            (this.controlValues.itemAmount > this.controlValues.minAmount || this.minusButtonRemove);
    }

    get minusButtonRemove() {
        return this.controlValues.itemAmount === this.controlValues.minAmount ||
            this.controlValues.itemAmount === 1;
    }

    get minusButtonStyle(): string {
        let minusButtonStyle = "RACBAmountButton";

        if (this.minusButtonEnabled) {
            minusButtonStyle += " RACBWaved";

            if (this.minusButtonRemove) {
                minusButtonStyle += " RACBRemove";
            }
        } else {
            minusButtonStyle += " RACBDisabled";
        }

        return minusButtonStyle;
    }

    get minusButtonCallback() {
        if (!this.minusButtonEnabled) {
            return () => {
            };
        }

        if (this.minusButtonRemove) {
            return this.controlValues.deleteArticleCallback;
        }

        return () => this.onIncreaseValue(-1);
    }

    get plusButtonStyleEnabled() {
        return this.controlValues.itemAmount < this.controlValues.maxAmount;
    }

    get plusButtonStyle(): string {
        let plusButtonStyle = "RACBAmountButton";

        if (this.plusButtonStyleEnabled) {
            plusButtonStyle += " RACBWaved";
        } else {
            plusButtonStyle += " RACBDisabled";
        }

        return plusButtonStyle;
    }

    get plusButtonCallback() {
        if (this.controlValues.disabled || !this.plusButtonStyleEnabled) {
            return null;
        }

        return () => this.onIncreaseValue(1);
    }

    get available() {
        return this.controlValues.maxAmount > 0 ||
            this.controlValues.itemAmount > 0;
    }

    get singleAmount() {
        return this.controlValues.maxAmount === 0 ||
            this.controlValues.maxAmount === 1 ||
            (this.controlValues.minAmount === this.controlValues.maxAmount);
    }

    get showInput() {
        return !this.singleAmount;
    }

    get showMinus() {
        return this.itemInCart && !this.singleAmount;
    }

    get showPlus() {
        return !this.singleAmount;
    }

    get showDeleteOnly() {
        return this.itemInCart && this.singleAmount;
    }

    get showControls() {
        return this.showPlus || this.showMinus || this.showDeleteOnly;
    }

    get notAvailableText() {
        return trans('articleBooking.notAvailable', this.locale, this.translations);
    }

    get textButtonStyleEnabled() {
        return this.controlValues.itemAmount === 0;
    }

    get textButtonStyle(): string {
        if (this.textButtonStyleEnabled) {
            return "RACBTextButtonContainer RACBTextButtonActive";
        }

        return "RACBTextButtonContainer";
    }

    get textButtonCallback() {
        if (this.controlValues.disabled || !this.textButtonStyleEnabled) {
            return null;
        }

        return () => this.onIncreaseValue(1);
    }

    render() {
        return (
            <div class={this.containerClass}>
                {this.available &&
                    <Fragment>
                        {this.showControls &&
                            <Fragment>
                                <div class="RACBControlContainer">
                                    {this.showDeleteOnly &&
                                        <div
                                            tabindex="0"
                                            class={"RACBAmountButton RACBWaved RACBRemoveHover"}
                                            onClick={this.minusButtonCallback}
                                        >
                                            <TrashIcon/>
                                        </div>
                                    }

                                    {this.showMinus &&
                                        <div
                                            tabindex="0"
                                            class={this.minusButtonStyle}
                                            onClick={this.minusButtonCallback}
                                        >
                                            <svg width="10" height="2" viewBox="0 0 10 2" fill="none"
                                                 xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 1H5H9" stroke-linecap="round"/>
                                            </svg>
                                        </div>
                                    }

                                    {this.showPlus &&
                                        <div
                                            tabindex="0"
                                            class={this.plusButtonStyle}
                                            onClick={this.plusButtonCallback}
                                        >
                                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                                                 xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 4.94092H5M9 4.94092H5M5 4.94092V1M5 4.94092V9"
                                                      stroke-linecap="round"/>
                                            </svg>
                                        </div>
                                    }
                                </div>

                                <div class="RACBDivider"/>
                            </Fragment>
                        }

                        {this.showInput &&
                            <Fragment>
                                <input
                                    class="RACBAmountInput"
                                    maxlength="3"
                                    type="number"
                                    value={String(this.controlValues.itemAmount)}
                                    onKeyPress={this.onValidateKey}
                                    onChange={this.onInputChange}
                                />

                                <div class="RACBDivider"/>
                            </Fragment>
                        }

                        <div
                            class={this.textButtonStyle}
                            tabindex="0"
                            onClick={this.textButtonCallback}
                        >
                            {this.controlValues.text}
                        </div>
                    </Fragment>
                }

                {!this.available &&
                    <div class="RACBTextButtonContainer RACBTextButtonNotAvailable">
                        <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M9.26795 2C10.0377 0.666667 11.9623 0.666667 12.7321 2L20.5263 15.5C21.2961 16.8333 20.3338 18.5 18.7942 18.5H3.20577C1.66617 18.5 0.70392 16.8333 1.47372 15.5L9.26795 2Z"
                                stroke-width="2"/>
                        </svg>

                        <span>
                            {this.notAvailableText}
                        </span>
                    </div>
                }

                {this.controlValues.disabled &&
                    <div class="RACBAmountWaiting"/>
                }
            </div>
        )
    }
}
